/*
 * The iPhone app's glue to iOS (does nothing on the website): the launch screen, the status bar colour,
 * tapping a notification, and coming back to the foreground.
 */
import { useEffect, useRef } from 'react';
import { App as CapApp } from '@capacitor/app';
import { PushNotifications } from '@capacitor/push-notifications';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { api } from './api.js';
import { NATIVE } from './platform.js';
import { useNav } from './router.js';
import { useStore } from './store.js';

/** A notification's link is an app path ("/order/ord_…"); anything else opens Home. */
export function pathFromLink(link: unknown): string {
  if (typeof link !== 'string' || !link) return '/';
  try {
    const u = new URL(link, 'https://app.invalid');
    return u.pathname.startsWith('/') ? `${u.pathname}${u.search}` : '/';
  } catch {
    return '/';
  }
}

export function NativeShell() {
  const s = useStore();
  const { navigate, back } = useNav();
  const nav = useRef({ navigate, back, refreshMe: s.refreshMe });
  nav.current = { navigate, back, refreshMe: s.refreshMe };

  // The launch screen is taken down by main.tsx as soon as the web app has painted its twin; this is the belt and braces.
  useEffect(() => {
    if (!NATIVE) return;
    const t = setTimeout(() => SplashScreen.hide({ fadeOutDuration: 0 }).catch(() => undefined), 1500);
    return () => clearTimeout(t);
  }, []);

  // Dark text on the cream: the app is light only (Saif, 22 Oct 2026), whatever the phone's own setting.
  useEffect(() => {
    if (!NATIVE) return;
    StatusBar.setStyle({ style: Style.Light }).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!NATIVE) return;
    const subs = [
      // Tapping a notification opens the screen it is about (the order, a message's link), and counts the tap.
      PushNotifications.addListener('pushNotificationActionPerformed', (a) => {
        const data = (a.notification.data ?? {}) as { link?: string; messageId?: string };
        if (data.messageId) api(`/api/me/messages/${encodeURIComponent(data.messageId)}/tapped`, { body: {} }).catch(() => undefined);
        nav.current.navigate(pathFromLink(data.link));
        nav.current.refreshMe().catch(() => undefined);
      }),
      // A notification while the app is open: no banner (capacitor.config.ts); the order screen catches up at once.
      PushNotifications.addListener('pushNotificationReceived', () => {
        nav.current.refreshMe().catch(() => undefined);
      }),
      CapApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) nav.current.refreshMe().catch(() => undefined);
      }),
      // Android's back button walks back through the app, like the on-screen back buttons.
      CapApp.addListener('backButton', () => nav.current.back()),
    ];
    return () => {
      for (const p of subs) p.then((h) => h.remove()).catch(() => undefined);
    };
  }, []);

  return null;
}
